// One foot-sorted group per actor. Weapons and sleeves cannot interleave with a neighbouring actor/building.
import { Container, Graphics, Sprite } from 'pixi.js';
import type { Look } from './art';
import { CREW } from './crew';
import { K } from './hd-kit';
import { characterBodyTexture, retainBodyTexture, releaseBodyTexture, weaponTexture, armRectangles, CHAR_SCALE, CHAR_RATIO as R } from './character-art';
import { createMotion, advanceMotion, motionEvent, rigPose, bladeTrail, weaponPoint, WEAPON_SHAPES, type MotionInput, type ArmPose } from './character-motion';
import { HELD_GUNS, heldFamily, heldGun, partsFromBits, type HeldGun } from './held-guns';
import { tex } from './art';
import type { Texture } from 'pixi.js';
import { muzzleFlash, WF } from './weapon-fx';

const heldTextures = new WeakMap<HeldGun, Texture>();
const heldTexture = (h: HeldGun) => { let t = heldTextures.get(h); if (!t) { t = tex(h.canvas); heldTextures.set(h, t); } return t; };

const color = (hex: string) => parseInt(hex.slice(1), 16);
export class CharacterRig {
  root = new Container();
  body = new Sprite(); weapon = new Sprite();
  farArm = new Graphics(); nearArm = new Graphics(); magazine = new Graphics(); flash = new Graphics();
  trail = new Graphics();
  motion = createMotion();
  muzzle?: { x: number; y: number };
  private tintColor = 0xffffff;
  private armKeys = new WeakMap<Graphics, string>();
  private pendingShot = false;
  constructor(parent: Container) {
    this.body.scale.set(CHAR_SCALE); this.body.anchor.set(0.5, 1);
    this.weapon.scale.set(CHAR_SCALE); this.weapon.visible = false;
    this.root.addChild(this.farArm, this.body, this.weapon, this.nearArm, this.trail, this.magazine, this.flash);
    parent.addChild(this.root);
  }
  event(k: string, time: number, weapon?: string) {
    motionEvent(this.motion, k, time, weapon);
    if (k === 'shot' && this.motion.shotAt === time) this.pendingShot = true;
  }
  update(l: Look, input: MotionInput, time: number, visible = true, lowFx = false, weaponFx = false, comfort = 1) {
    if (weaponFx && this.pendingShot && this.motion.weapon === input.weapon && time - this.motion.shotAt <= WF.eventGraceSec) this.motion.shotAt = time;
    this.pendingShot = false;
    const p = advanceMotion(this.motion, input, time), feet = input.y + 5;
    const family = HELD_GUNS && p.kind !== 'none' ? heldFamily(this.motion.weapon, p.kind) : undefined;
    const pose = rigPose(p, family);
    this.root.position.set(input.x, p.down ? input.y : feet - p.vault * 4);
    this.root.zIndex = feet; this.root.visible = visible; this.root.rotation = p.down ? Math.PI / 2 : 0;
    this.muzzle = pose.weaponVisible && p.kind !== 'katana' && p.kind !== 'tanto' && p.kind !== 'naginata'
      ? { x: this.root.x + pose.muzzle.x * R, y: this.root.y + pose.muzzle.y * R } : undefined;
    if (!visible) return;
    const texture = characterBodyTexture(l, p);
    if (texture !== this.body.texture) {
      retainBodyTexture(texture); releaseBodyTexture(this.body.texture); this.body.texture = texture;
    }
    this.body.anchor.set(0.5, p.down ? 0.5 : 1);
    this.farArm.visible = this.nearArm.visible = !p.down;
    const far = pose.right.near ? pose.left : pose.right, near = pose.right.near ? pose.right : pose.left;
    this.arm(this.farArm, far, l); this.arm(this.nearArm, near, l);
    this.weapon.visible = pose.weaponVisible;
    let muzzle = pose.muzzle;
    if (pose.kind !== 'none') {
      const shape = WEAPON_SHAPES[pose.kind], magOut = p.reload >= 0.22 && p.reload < 0.67;
      const flip = family && Math.cos(pose.angle) < 0 ? -1 : 1; // sights stay on top when aiming west
      if (family) {
        const held = heldGun(family, magOut, partsFromBits(input.parts)); // cached per family, mag and parts
        this.weapon.texture = heldTexture(held);
        this.weapon.anchor.set(held.grip.x / held.canvas.width, held.grip.y / held.canvas.height);
        // the flash sits on this sprite's own muzzle (hd px are half a world px; rig units are world px / R)
        muzzle = weaponPoint(pose.grip, pose.angle, (held.muzzle.x - held.grip.x) / 2 / R, flip * (held.muzzle.y - held.grip.y) / 2 / R);
      } else {
        this.weapon.texture = weaponTexture(pose.kind, CREW[l.crew ?? 0]?.color ?? K.cyan, magOut);
        this.weapon.anchor.set(shape.grip.x / shape.w, shape.grip.y / shape.h);
      }
      this.weapon.scale.set(CHAR_SCALE, CHAR_SCALE * flip);
      this.weapon.position.set(pose.grip.x * R, pose.grip.y * R); this.weapon.rotation = pose.angle;
    }
    if (this.muzzle) this.muzzle = { x: this.root.x + muzzle.x * R, y: this.root.y + muzzle.y * R };
    // The far hand is behind the torso. A weapon aimed north also passes behind the torso; nearer fingers stay on top.
    this.root.setChildIndex(this.farArm, 0);
    this.root.setChildIndex(this.weapon, pose.weaponBack ? 1 : 2);
    this.root.setChildIndex(this.body, pose.weaponBack ? 2 : 1);
    this.root.setChildIndex(this.nearArm, 3);
    this.trail.clear();
    const tail = lowFx && !weaponFx ? [] : bladeTrail(p), bladeColor = pose.kind === 'tanto' ? CREW[l.crew ?? 0]?.color ?? K.cyan : weaponFx ? K.white : K.cyan;
    if (tail.length) {
      const q = (n: number) => weaponFx ? Math.round(n) : n;
      this.trail.moveTo(q(tail[0].x * R), q(tail[0].y * R)); for (const tip of tail.slice(1)) this.trail.lineTo(q(tip.x * R), q(tip.y * R));
      this.trail.stroke({ color: color(bladeColor), width: weaponFx ? pose.kind === 'tanto' ? 2 : 1 : 1.5, alpha: weaponFx ? .6 * comfort : .5 });
      if (!weaponFx) {
        this.trail.moveTo(tail[0].x * R, tail[0].y * R); for (const tip of tail.slice(1)) this.trail.lineTo(tip.x * R, tip.y * R);
        this.trail.stroke({ color: color(K.white), width: .5, alpha: .85 });
      }
    }
    this.magazine.clear();
    if (pose.magazine && !p.down) {
      this.magazine.rect(-1, 0, 2, 3).fill(color(K.metalLo)).rect(-1, 0, 0.5, 2.5).fill(color(K.metalHi));
      this.magazine.position.set(pose.magazine.x * R, pose.magazine.y * R);
    }
    this.flash.clear();
    if (pose.weaponVisible && (weaponFx ? time - this.motion.shotAt >= 0 && time - this.motion.shotAt < WF.flashSec : pose.flash && !lowFx)) {
      if (weaponFx) muzzleFlash(this.flash, this.motion.weapon, comfort);
      else { this.flash.poly([0, -0.5, 2, -2.5, 2, -1, 5, 0, 2, 1, 2, 2.5, 0, 0.5]).fill(color(K.amber)); this.flash.rect(0, -0.5, 2.5, 1).fill(color(K.white)); }
      this.flash.position.set(weaponFx ? Math.round(muzzle.x * R) : muzzle.x * R, weaponFx ? Math.round(muzzle.y * R) : muzzle.y * R); this.flash.rotation = pose.angle;
    }
    if (p.kind === 'coilgun' && p.charge > 0 && pose.weaponVisible && !lowFx) {
      // Small charging lamps on the receiver, palette locked; no extra world light or new projectile rules.
      const ca = Math.cos(pose.angle), sa = Math.sin(pose.angle);
      for (let i = 0; i < Math.ceil(p.charge * 3); i++) {
        this.magazine.rect(pose.grip.x * R + ca * (3 + i) + sa - 0.25, pose.grip.y * R + sa * (3 + i) - ca - 0.25, 0.5, 0.5).fill(color(i === 2 ? K.white : K.cyan));
      }
      this.magazine.position.set(0, 0);
    }
    this.tint(this.tintColor, this.root.alpha);
  }
  private arm(g: Graphics, p: ArmPose, l: Look) {
    const key = `${l.jacket}|${l.skin}|${l.npc}|${p.near}|${[p.shoulder, p.elbow, p.hand].flatMap(v => [Math.round(v.x * 2 * R), Math.round(v.y * 2 * R)]).join(',')}`;
    if (this.armKeys.get(g) === key) return; this.armKeys.set(g, key);
    g.clear();
    for (const r of armRectangles(p, l)) g.rect(r.x / 2, r.y / 2, r.w / 2, r.h / 2).fill(color(r.color));
  }
  tint(tint: number, alpha = 1) {
    this.tintColor = tint; this.root.alpha = alpha;
    for (const part of [this.body, this.weapon, this.farArm, this.nearArm, this.magazine]) part.tint = tint;
  }
  destroy() { this.muzzle = undefined; releaseBodyTexture(this.body.texture); this.root.destroy({ children: true }); }
}
